// @ts-strict-ignore
import React, { useEffect, useMemo, useRef, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useFlux } from '@/hybrid/core/hooks/useFlux.hook';
import { fetchAssetGroups, fetchPinnedItems, fetchRecentlyAccessedItems } from '@/workbook/workbook.actions';
import { FormControl } from 'react-bootstrap';
import { Icon } from '@/hybrid/core/Icon.atom';
import { IconWithSpinner } from '@/hybrid/core/IconWithSpinner.atom';
import { ErrorWithBackground } from '@/hybrid/core/ErrorWithBackground.atom';
import { SearchResult } from '@/hybrid/search/SearchResult.molecule';
import { SearchGroup } from '@/hybrid/search/SearchGroup.molecule';
import InfiniteScroll from 'react-infinite-scroller';
import { useFluxPath } from '@/hybrid/core/hooks/useFluxPath.hook';
import { SearchResultAssetIcons } from '@/hybrid/assetGroupEditor/SearchResultAssetIcons.molecule';
import { SearchResultIcons } from '@/hybrid/search/SearchResultIcons.molecule';
import { SearchFilters } from '@/hybrid/search/SearchFilters.molecule';
import { TextButton } from '@/hybrid/core/TextButton.atom';
import { KEY_CODES } from '@/main/app.constants';
import { AssetGroupMultiSelect } from '@/hybrid/search/multiSelect/AssetGroupMultiSelect.molecule';
import { TrendMultiSelect } from '@/hybrid/search/multiSelect/TrendMultiSelect.molecule';
import { isAsset, isDatafile } from '@/hybrid/utilities/utilities';
import { errorToast } from '@/hybrid/utilities/toast.utilities';
import classNames from 'classnames';
import { sqLicenseManagementStore, sqWorkbookStore, sqWorksheetStore } from '@/core/core.stores';
import { WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import { ASSET_GROUP_VIEW } from '@/worksheet/worksheet.constants';
import { SearchModes, SearchPanes } from '@/search/search.constants';
import {
  clear,
  exploreAssetSearchActions,
  fetchItemUsages,
  getStoreForPane,
  initializeSearchActions,
  loadNextPageSearchActions,
  reset,
  setSelectAllItems,
} from '@/search/search.actions';

interface SearchWidgetProps {
  pane: SearchPanes;
  searchTypes: string[];
  iconClasses?: string;
  scopeIds?: string[];
  excludeGloballyScoped?: boolean;
  restrictExploration?: boolean;
  indicateSelection?: boolean;
  selectedItemId?: string;
  onItemClick?: (item: any, mode?: SearchModes, pane?: SearchPanes) => Promise<any> | void;
  searchResultIcons?: (item) => Promise<JSX.Element | void>;
  showOnlyResults?: boolean;
  allowAssetReplacement: boolean;
  isSelectingAsset?: boolean;
  searchResultsClassNames?: string;
  // Used to determine whether or not to clear the modal stores on closing the search widget
  modalId?: string;
}

let lastModalId = '';

export const SearchWidget: React.FunctionComponent<SearchWidgetProps> = (props) => {
  const {
    pane,
    searchTypes,
    iconClasses,
    scopeIds,
    excludeGloballyScoped = false,
    onItemClick = _.noop,
    allowAssetReplacement,
    searchResultIcons = defaultSearchResultIcons,
    showOnlyResults = false,
    restrictExploration = false,
    indicateSelection = false,
    selectedItemId = undefined,
    isSelectingAsset = false,
    modalId = undefined,
    searchResultsClassNames,
  } = props;
  if (lastModalId === '') {
    lastModalId = modalId;
  }

  const { t } = useTranslation();
  const isAssetGroupView = useFluxPath(sqWorksheetStore, () => sqWorksheetStore.view.key === ASSET_GROUP_VIEW.key);
  const [currentItems, setCurrentItems] = useState([]);
  const [placeholderCategory, setPlaceholderCategory] = useState(undefined);
  const [nameFilter, setNameFilter] = useState('');
  const LIMIT_IN_OVERVIEW = 8;
  const store = getStoreForPane(pane);
  const scrollParentRef = useRef(null);

  const selectAllChecked = useFluxPath(store, () => store.selectAllChecked);
  const selectedItemIds = useFluxPath(store, () => store.selectedItemIds);
  // NOTE: please do not "simplify" the below to useFlux instead. We're intentionally using useFluxPath to avoid
  // performance hits when typing into the search field.
  const hasNextPage = useFluxPath(store, (store) => store.hasNextPage);
  const mode = useFluxPath(store, (store) => store.mode);
  const searching = useFluxPath(store, (store) => store.searching);
  const isPaginating = useFluxPath(store, (store) => store.isPaginating);
  const currentAsset = useFluxPath(store, (store) => store.currentAsset);
  const breadcrumbs = useFluxPath(store, (store) => store.breadcrumbs);
  const items = useFluxPath(store, (store) => store.items);
  const isAsyncInitialized = useFluxPath(store, (store) => store.isAsyncInitialized);
  const highlightItemId = useFluxPath(store, (store) => store.highlightItemId);
  const isAllUnselectable = useMemo(() => {
    if (isAssetGroupView) {
      return false;
    }

    for (let i = 0; i < currentItems?.length; i++) {
      const item = currentItems[i];
      if (!(item.hasChildren || isAsset(item))) {
        return false;
      }
    }

    return true;
  }, [currentItems, isAssetGroupView]);

  const {
    pinned,
    recentlyAccessed,
    recentlyAccessedLoadStatus,
    pinnedLoadStatus,
    assetGroups,
    assetGroupsLoadStatus,
    workbookId,
    workbookDisplay,
  } = useFlux(sqWorkbookStore);

  useEffect(() => {
    switch (mode) {
      case 'tree':
      case 'search':
        setPlaceholderCategory(_.get(_.last(_.reject(breadcrumbs, ['type', 'VIEW_MODE'])), 'name'));
        setCurrentItems(items);
        break;
      case 'pinned':
        setPlaceholderCategory('SEARCH_DATA.PINNED');
        setCurrentItems(pinned);
        break;
      case 'recent':
        setPlaceholderCategory('SEARCH_DATA.RECENTLY_ACCESSED');
        setCurrentItems(recentlyAccessed);
        break;
      case 'asset-groups':
        setPlaceholderCategory('SEARCH_DATA.ASSET_GROUPS');
        setCurrentItems(assetGroups);
        break;
      case 'usages':
        setPlaceholderCategory('SEARCH_DATA.USAGES');
        setCurrentItems(items);
        break;
      default:
        setPlaceholderCategory(undefined);
        setCurrentItems(items);
        break;
    }
  }, [mode, items, pinned, recentlyAccessed, assetGroups, selectedItemIds, selectAllChecked]);

  useEffect(() => {
    if (!isAsyncInitialized) {
      // Not ideal to initiate the async fetch from a useEffect, but there are so many modals that open this widget
      // up that this seems better than the alternative of having all the modals call it when the modal opens.
      // Note that this only gets called for modals, stateSynchronizer handles the initialization for the main panel
      // to enable fast follow to work (CRAB-26634)
      initializeSearchActions(pane, searchTypes, restrictExploration, scopeIds, excludeGloballyScoped).catch((error) =>
        errorToast({ httpResponseOrError: error }),
      );
    }

    if (pane === 'modal' && (modalId !== lastModalId || showOverviewMode)) {
      reset(pane);
      initializeSearchActions(pane, searchTypes, restrictExploration, scopeIds, excludeGloballyScoped).catch((error) =>
        errorToast({ httpResponseOrError: error }),
      );
    }

    return () => {
      lastModalId = modalId;
    };
  }, []);

  const limit = mode === 'overview' ? LIMIT_IN_OVERVIEW : undefined;
  const isDoneLoadingResults = () => !searching && !isPaginating;
  const hasNoResults = () => isDoneLoadingResults() && items.length === 0 && currentItems.length === 0;

  const filterByRestriction = (item) => (restrictExploration ? _.includes(searchTypes, item.type) : true);

  const filterByName = (item) =>
    _.includes(['overview', 'search', 'tree'], mode)
      ? true
      : item?.name.toLowerCase().includes(nameFilter.toLowerCase());

  const limitAndFilterItems = (items) =>
    _.chain(items).filter(filterByName).filter(filterByRestriction).slice(0, limit).value();

  const exploreAsset = (asset) => {
    return exploreAssetSearchActions(pane, asset.id, searchTypes, restrictExploration, scopeIds, excludeGloballyScoped);
  };

  const fetchUsages = (item) => {
    if (item.id) {
      return fetchItemUsages(item, pane, workbookId ? [workbookId] : [], false);
    } else {
      setNameFilter('');
      return exploreAssetSearchActions(
        pane,
        item.id,
        searchTypes,
        restrictExploration,
        undefined,
        excludeGloballyScoped,
      );
    }
  };

  const viewItem = (item) => {
    if (isAsset(item) || isDatafile(item) || item.hasChildren) {
      exploreAsset(item);
    } else if (!isAssetGroupView || pane !== 'main') {
      onItemClick(item, mode, pane);
    }
  };

  const loadNextPage = () => {
    if (!searching && !isPaginating && hasNextPage) {
      return loadNextPageSearchActions(pane, searchTypes, restrictExploration, scopeIds, excludeGloballyScoped).then(
        ({ items: newItems }) => {
          if (isAssetGroupView) {
            setSelectAllItems(undefined);
          } else {
            const hasLeafItem = !!newItems?.find((item) => !(item.hasChildren || isAsset(item)));
            hasLeafItem && setSelectAllItems(undefined);
          }
        },
      );
    }
  };

  function defaultSearchResultIcons(item) {
    return Promise.resolve(
      <>
        {allowAssetReplacement && <SearchResultAssetIcons item={item} isAssetGroupView={isAssetGroupView} />}
        {!isAssetGroupView && (
          <SearchResultIcons
            item={item}
            pinned={pinned}
            workbookDisplay={workbookDisplay}
            workbookId={workbookId}
            onItemClick={onItemClick}
            allowAssetReplacement={allowAssetReplacement}
            restrictExploration={restrictExploration}
          />
        )}
      </>,
    );
  }

  // In pinned, recently accessed or asset group mode, users can filter by the name, pulling this out of the
  // <SearchFilters/> component allows us to not have to rerender every time a user types in an input (CRAB-24095)
  const renderOnlyNameFilter = (
    <div className="card card-default">
      <div className="card-body" data-testid="searchPanel">
        <FormControl
          className="mb10 height-34"
          id="searchInput"
          name="nameFilter"
          data-testid="nameOnlyFilter"
          placeholder={t('SEARCH_DATA.NAME_CONTAINS')}
          onChange={(name) => setNameFilter(name.target.value)}
          value={nameFilter}
        />
        <TextButton
          label="SEARCH_DATA.RESET"
          onClick={() => {
            clear(pane, searchTypes, restrictExploration, scopeIds, excludeGloballyScoped);
            setNameFilter('');
          }}
          testId="resetButton"
        />
      </div>
    </div>
  );

  const showSearchFilters = _.includes(['tree', 'overview', 'search'], mode);
  const showUsages = mode === 'usages';
  const showAssetTrees = mode !== 'overview' && !showUsages;
  const showOverviewMode = mode === 'overview';
  const showSpinner = !isDoneLoadingResults();
  const showError = hasNoResults() && !currentAsset && !showUsages;
  const showUsagesError = hasNoResults() && showUsages;
  const showPinnedItems = !showOnlyResults && !showUsages;
  const showAssetGroups = !showUsages;
  const showRecentlyAccessed = !showOnlyResults && !showUsages;

  const showInfinite = !_.includes(['pinned', 'recent', 'asset-groups', 'usages'], mode);
  const showErrorNoResults =
    (isDoneLoadingResults() && !hasNoResults() && _.filter(currentItems, filterByName).length === 0) ||
    (hasNoResults() && currentAsset);
  const placeholderCategoryTranslated = t(placeholderCategory);
  return (
    <div className="flexFill flexRowContainer overflowHidden searchWidget" data-testid="searchWidget">
      {showSearchFilters ? (
        <SearchFilters
          pane={pane}
          searchTypes={searchTypes}
          scopeIds={scopeIds}
          excludeGloballyScoped={excludeGloballyScoped}
        />
      ) : (
        renderOnlyNameFilter
      )}

      {showAssetTrees && (
        <ul className="unstyled-list searchBreadcrumbs flexColumnContainer flexWrap flexNoGrowNoShrink mb0 heading">
          {_.map(breadcrumbs, (breadcrumb, index) => {
            const isLast = breadcrumbs.length - 1 === _.toNumber(index);
            return (
              <li
                tabIndex={isLast ? undefined : 0}
                onKeyUp={(e) =>
                  e.keyCode === KEY_CODES.ENTER && !isLast && !breadcrumb.isLoading && exploreAsset(breadcrumb)
                }
                onClick={() => !breadcrumb.isLoading && !isLast && exploreAsset(breadcrumb)}
                key={breadcrumb.id}>
                {breadcrumb.isLoading && <IconWithSpinner spinning={true} />}
                {!breadcrumb.id && (
                  <Icon icon="fa-home" large={true} extraClassNames="pl5" testId="breadcrumbHomeIcon" />
                )}
                <span className="fs15">{t(breadcrumb.name)} </span>
                {!breadcrumb.isLoading && breadcrumbs.length === 1 && (
                  <span className="fs15 pl5">{t('SEARCH_DATA.ASSET_TREES')}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {showError && <ErrorWithBackground errorText="SEARCH_DATA.NO_RESULTS" dismissible={false} type="Warning" />}

      {showInfinite && !showOverviewMode && !showErrorNoResults && pane !== 'modal' ? (
        <div className="pt10 pb10 itemRow">
          {isAssetGroupView ? (
            <AssetGroupMultiSelect pane={pane} items={currentItems} />
          ) : (
            <TrendMultiSelect pane={pane} items={currentItems} />
          )}
        </div>
      ) : null}

      <div
        id="spec-search-results"
        data-testid="spec-search-results"
        className={classNames('flexRowContainer overflowAuto height-maximum', searchResultsClassNames)}
        ref={scrollParentRef}>
        {showUsages && (
          <>
            <ul className="unstyled-list searchBreadcrumbs flexColumnContainer flexWrap flexNoGrowNoShrink mb0 heading">
              {_.map(breadcrumbs, (breadcrumb, index) => {
                const isTitle = breadcrumb.id === 'ITEM_USAGES';
                const isItemOrHomeBreadcrumb = !(breadcrumbs.length - 1 === _.toNumber(index) || isTitle);
                return (
                  <li
                    tabIndex={!isItemOrHomeBreadcrumb ? undefined : 0}
                    onKeyUp={(e) =>
                      e.keyCode === 13 && isItemOrHomeBreadcrumb && !breadcrumb.isLoading && fetchUsages(breadcrumb)
                    }
                    onClick={() => !breadcrumb.isLoading && isItemOrHomeBreadcrumb && fetchUsages(breadcrumb)}
                    key={breadcrumb.id}>
                    {breadcrumb.isLoading && <IconWithSpinner spinning={true} />}
                    {!breadcrumb.id && (
                      <Icon icon="fa-home" large={true} extraClassNames="pl5" testId="usagesHomeIcon" />
                    )}
                    {isTitle && <Icon icon="fa fc-usage" extraClassNames="pl5 pr5 sq-icon-dark-gray " />}
                    <span className={isTitle ? 'text-bold title fs15' : 'fs15'}>{t(breadcrumb.name)} </span>
                  </li>
                );
              })}
            </ul>
            <SearchGroup
              isSelectingAsset={isSelectingAsset}
              loadStatus={searching}
              label="USAGES"
              searchMode="usages"
              searchTypes={[]}
              scopeIds={scopeIds}
              restrictExploration={restrictExploration}
              pane={pane}
              onErrorClick={_.noop}
              onClickItem={viewItem}
              searchResultIcons={searchResultIcons}
              mode={mode}
              items={limitAndFilterItems(items)}
              indicateSelection={indicateSelection}
              selectedItemId={selectedItemId}
            />
            {showSpinner && (
              <div className="pt10 pb10 flexNoGrowNoShrink flexColumnContainer flexCenter">
                <IconWithSpinner spinning={true} large={true} />
              </div>
            )}
          </>
        )}

        {showUsagesError && (
          <ErrorWithBackground errorText="SEARCH_DATA.NO_RESULTS" dismissible={false} type="Warning" />
        )}

        {showPinnedItems && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            loadStatus={pinnedLoadStatus}
            label="PINNED"
            pane={pane}
            searchMode="pinned"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            onErrorClick={() => fetchPinnedItems(workbookId)}
            onClickItem={viewItem}
            searchResultIcons={searchResultIcons}
            mode={mode}
            items={limitAndFilterItems(pinned)}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
          />
        )}

        {showAssetGroups && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            loadStatus={assetGroupsLoadStatus}
            label="ASSET_GROUPS"
            searchMode="asset-groups"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            onErrorClick={() => fetchAssetGroups([workbookId])}
            pane={pane}
            onClickItem={viewItem}
            searchResultIcons={searchResultIcons}
            mode={mode}
            noItemsMessage={
              sqLicenseManagementStore.hasAssetGroups() &&
              sqWorkbookStore.workbookDisplay === WORKBOOK_DISPLAY.EDIT &&
              'SEARCH_DATA.NO_ASSET_GROUPS'
            }
            items={limitAndFilterItems(assetGroups)}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
          />
        )}

        {showRecentlyAccessed && (
          <SearchGroup
            isSelectingAsset={isSelectingAsset}
            loadStatus={recentlyAccessedLoadStatus}
            label="RECENTLY_ACCESSED"
            searchMode="recent"
            searchTypes={searchTypes}
            scopeIds={scopeIds}
            restrictExploration={restrictExploration}
            pane={pane}
            onErrorClick={() => fetchRecentlyAccessedItems(workbookId)}
            onClickItem={viewItem}
            searchResultIcons={searchResultIcons}
            mode={mode}
            items={limitAndFilterItems(recentlyAccessed)}
            indicateSelection={indicateSelection}
            selectedItemId={selectedItemId}
          />
        )}

        {showOverviewMode && (
          <div className="flexColumnContainer flexWrap flexNoGrowNoShrink ml2 mb0 heading">
            <span className="fs15 text-interactive">{t('SEARCH_DATA.ASSET_TREES')}</span>
          </div>
        )}

        {showInfinite && (
          <>
            <InfiniteScroll
              pageStart={0}
              hasMore={hasNextPage}
              loadMore={loadNextPage}
              useWindow={false}
              initialLoad={false}
              getScrollParent={() => scrollParentRef.current}>
              {_.map(currentItems, (item) => (
                <SearchResult
                  isSelectingAsset={isSelectingAsset}
                  customIconClasses={iconClasses}
                  key={item.id}
                  items={currentItems}
                  iconsPromise={searchResultIcons(item)}
                  item={item}
                  onClickItem={() => viewItem(item)}
                  isSelected={indicateSelection && selectedItemId === item.id}
                  itemSelected={_.includes(selectedItemIds, item.id)}
                  showItemSelection={!showOverviewMode && pane !== 'modal'}
                  isAllUnselectable={isAllUnselectable}
                  highlightItem={highlightItemId === item.id}
                />
              ))}
            </InfiniteScroll>

            {showSpinner && (
              <div className="pt10 pb10 flexNoGrowNoShrink flexColumnContainer flexCenter">
                <IconWithSpinner spinning={true} large={true} />
              </div>
            )}
          </>
        )}
      </div>

      {showErrorNoResults && !showUsages && (
        <ErrorWithBackground
          type="Warning"
          dismissible={false}
          errorText="SEARCH_DATA.NO_RESULTS_WITHIN_CATEGORY"
          errorParameters={{ category: placeholderCategoryTranslated }}
        />
      )}
    </div>
  );
};
